import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Images, Info, Search, Ticket, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import posthog from 'posthog-js';
import { trackEventPass } from '@/utils/eventPassTracking';
import { PhotoLikeBadge } from '@/components/PhotoLikeBadge';
import { photoIdFromUrl, usePhotoLikeCounts } from '@/utils/photoLikes';
import { cn } from '@/lib/utils';
import { addEventPass, EVENT_PASS_MIN_RACES, getCart, PRICES, riderKey } from '@/utils/cart';
import { fetchEventPassRiders, fetchPendingClasses, fetchPassPhotos, mixPreviews, normalize, PassRider } from '@/utils/eventPassLookup';
import { EventPassGallery } from './EventPassGallery';

interface Props {
  eventUuid: string;
  eventSwissId: string;
  eventName: string;
  onOpenRace: (swissRaceId: string) => void;
}

export const useEventPassRiders = (eventUuid: string) =>
  useQuery({
    queryKey: ['event-pass-riders', eventUuid],
    queryFn: () => fetchEventPassRiders(eventUuid),
    staleTime: 5 * 60_000,
    enabled: !!eventUuid,
  });

export const usePassPhotos = (key: string | undefined, entries: { participantId: string; raceCategory: string; horseName: string }[], eventSwissId: string) =>
  useQuery({
    queryKey: ['event-pass-photos', eventSwissId, key],
    queryFn: () => fetchPassPhotos(entries, eventSwissId),
    staleTime: 5 * 60_000,
    enabled: !!key && entries.length > 0,
  });

const chf = (n: number) => `CHF ${n}.-`;
const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low';
const crossFade = 'absolute inset-0 h-4 w-4 transition-[opacity,transform] duration-200 [transition-timing-function:cubic-bezier(0.2,0,0,1)]';

const Kicker = ({ t }: { t: (k: string) => string }) => (
  <div className="flex items-center gap-2.5">
    <span className="inline-flex h-6 items-center rounded-full bg-primary px-2.5 text-xs font-semibold text-primary-foreground">
      {t('eventPass.new')}
    </span>
    <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{t('eventPass.ticketKicker')}</span>
  </div>
);

export const EventPassTicket = ({ eventUuid, eventSwissId, eventName, onOpenRace }: Props) => {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<PassRider | null>(null);
  const [active, setActive] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const [cartTick, setCartTick] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: riders = [], isLoading } = useEventPassRiders(eventUuid);
  const qc = useQueryClient();
  const { data: pending = 0 } = useQuery({ queryKey: ['event-pass-pending', eventUuid], queryFn: () => fetchPendingClasses(eventUuid), staleTime: 5 * 60_000, enabled: !!eventUuid });
  const Notice = ({ text }: { text: string }) => (
    <p className="flex items-start gap-2 rounded-lg bg-surface-container-low px-3 py-2.5 text-sm text-on-surface [text-wrap:pretty]">
      <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{text}
    </p>
  );
  const [gallery, setGallery] = useState<{ ci: number; pi: number } | null>(null);
  const { data: passPhotos, isLoading: photosLoading } = usePassPhotos(selected?.key, selected?.classes || [], eventSwissId);
  const allPassUrls = useMemo(() => (passPhotos || []).flatMap(c => c.urls), [passPhotos]);
  const likeIds = useMemo(() => allPassUrls.map(photoIdFromUrl), [allPassUrls]);
  const likeCounts = usePhotoLikeCounts(eventSwissId, likeIds);
  useEffect(() => {
    if (selected && passPhotos?.length) trackEventPass('previews_shown', { event_id: eventSwissId, source: 'event_ticket', classes: passPhotos.length, photos: allPassUrls.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passPhotos, selected?.key]);
  const prefetch = (r: PassRider) => qc.prefetchQuery({
    queryKey: ['event-pass-photos', eventSwissId, r.key],
    queryFn: () => fetchPassPhotos(r.classes, eventSwissId),
    staleTime: 5 * 60_000,
  });

  const eligibleCount = riders.filter(r => r.classes.length >= EVENT_PASS_MIN_RACES).length;

  useEffect(() => {
    const h = () => setCartTick(x => x + 1);
    window.addEventListener('cartUpdated', h);
    return () => window.removeEventListener('cartUpdated', h);
  }, []);

  useEffect(() => {
    if (open) {
      posthog.__loaded && posthog.capture('event_pass_viewed', { event_id: eventSwissId, source: 'event_ticket' });
      trackEventPass('ticket_opened', { event_id: eventSwissId, source: 'event_ticket', eligible_riders: eligibleCount, pending_classes: pending });
      inputRef.current?.focus();
    }
  }, [open, eventSwissId]);

  const matches = useMemo(() => {
    const q = normalize(query);
    if (q.length < 2) return [];
    return riders.filter(r => normalize(r.riderName).includes(q)).slice(0, 8);
  }, [query, riders]);

  useEffect(() => setActive(0), [matches]);
  useEffect(() => {
    if (selected || normalize(query).length < 2) return;
    const id = window.setTimeout(() => {
      trackEventPass('searched', { event_id: eventSwissId, source: 'event_ticket', query_length: query.length, results: matches.length });
      if (matches.length === 0) trackEventPass('no_results', { event_id: eventSwissId, source: 'event_ticket', query_length: query.length });
    }, 800);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => { if (matches[active]) prefetch(matches[active]); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, active]);
  // Fit the results list into the space between the field and the on-screen keyboard
  const [listMax, setListMax] = useState(320);
  useEffect(() => {
    if (!listOpen) return;
    const vv = window.visualViewport;
    const fit = () => {
      const el = inputRef.current;
      if (!el) return;
      const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const space = visibleBottom - el.getBoundingClientRect().bottom - 20;
      setListMax(Math.max(132, Math.min(320, Math.floor(space))));
    };
    fit();
    vv?.addEventListener('resize', fit);
    vv?.addEventListener('scroll', fit);
    window.addEventListener('scroll', fit, { passive: true });
    return () => {
      vv?.removeEventListener('resize', fit);
      vv?.removeEventListener('scroll', fit);
      window.removeEventListener('scroll', fit);
    };
  }, [listOpen]);

  const inCart = useMemo(() => {
    if (!selected) return false;
    return getCart().items.some(i => i.type === 'event_pass' && i.eventId === eventSwissId &&
      riderKey(i.swissEquestrianRiderId, i.participantName) === selected.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, eventSwissId, cartTick]);

  if (!isLoading && eligibleCount === 0) return null;

  const fullPrice = selected ? selected.classes.length * PRICES.COLLECTION : 0;
  const savings = fullPrice - PRICES.EVENT_PASS;
  const eligible = selected && selected.classes.length >= EVENT_PASS_MIN_RACES;

  const handleAdd = () => {
    if (!selected || inCart) return;
    trackEventPass('add_clicked', { event_id: eventSwissId, source: gallery ? 'gallery' : 'event_ticket', classes: selected.classes.length, photos: selected.totalPhotos, price: PRICES.EVENT_PASS, full_price: fullPrice });
    addEventPass({
      source: gallery ? 'gallery' : 'event_ticket',
      swissEquestrianRiderId: selected.swissRiderId,
      participantName: selected.riderName,
      eventId: eventSwissId,
      eventName,
      entries: selected.classes.map(({ participantId, raceId, raceCategory, horseName }) => ({ participantId, raceId, raceCategory, horseName })),
    });
  };

  const close = () => { setOpen(false); setSelected(null); setQuery(''); };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { if (showList) setListOpen(false); else close(); return; }
    if (!showList) setListOpen(true);
    if (!matches.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => (i + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => (i - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(matches[active], 'keyboard'); }
  };

  const showList = listOpen && !selected && query.length > 0;
  const pick = (r: PassRider, method: 'click' | 'keyboard' = 'click') => {
    setSelected(r); setQuery(r.riderName); setListOpen(false);
    inputRef.current?.blur(); // dismiss the phone keyboard so the pass is visible
    const ok = r.classes.length >= EVENT_PASS_MIN_RACES;
    trackEventPass('rider_selected', { event_id: eventSwissId, source: 'event_ticket', method, classes: r.classes.length, photos: r.totalPhotos, eligible: ok, position: matches.indexOf(r) });
    if (!ok) trackEventPass('single_class_shown', { event_id: eventSwissId, source: 'event_ticket', photos: r.totalPhotos });
  };
  const openGallery = (ci: number, pi: number, from: 'preview' | 'view_all' | 'class_row') => {
    setGallery({ ci, pi });
    trackEventPass('gallery_opened', { event_id: eventSwissId, source: 'event_ticket', from, start_class: ci + 1, start_photo: pi + 1, classes: selected?.classes.length, photos: selected?.totalPhotos });
  };
  const highlight = (name: string) => {
    const q = normalize(query);
    const i = normalize(name).indexOf(q);
    if (i < 0 || q.length < 2) return name;
    return <>{name.slice(0, i)}<mark className="bg-transparent font-semibold text-primary">{name.slice(i, i + q.length)}</mark>{name.slice(i + q.length)}</>;
  };

  const hint = isLoading ? t('eventPass.loading')
    : normalize(query).length < 2 ? t('eventPass.searchHint')
    : matches.length === 0 ? t('eventPass.noResults') : null;

  return (
    <section className="mb-14" aria-label={t('eventPass.ticketKicker')}>
      <div className="rounded-2xl bg-surface-container-low p-2">
        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn('group grid w-full grid-cols-1 gap-2 rounded-2xl text-left md:grid-cols-[1fr_18rem]', focusRing)}
          >
            <div className="flex flex-col gap-4 px-5 py-6 md:px-8 md:py-8">
              <Kicker t={t} />
              <h2 className="max-w-[24ch] font-serif text-3xl italic leading-tight text-primary [text-wrap:balance] md:text-4xl">
                {t('eventPass.ticketTitle')}
              </h2>
              <p className="text-sm text-muted-foreground [text-wrap:pretty]">{eventName}</p>
              {pending > 0 && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Info aria-hidden className="h-4 w-4 shrink-0" />{t('eventPass.pendingShort')}</p>}
            </div>

            <div className="flex items-center justify-between gap-4 rounded-lg bg-surface-container-lowest p-5 md:flex-col md:items-start md:justify-center md:p-6">
              <div>
                <p className="font-serif text-4xl leading-none tabular-nums text-primary">{chf(PRICES.EVENT_PASS)}</p>
                <p className="mt-2 text-sm text-muted-foreground">{t('eventPass.perRider')}</p>
              </div>
              <span className="inline-flex h-11 shrink-0 items-center gap-1 rounded-md bg-primary pl-5 pr-4 text-sm font-semibold text-primary-foreground transition-[transform,background-color] duration-150 group-hover:bg-primary-container group-active:scale-[0.96]">
                {t('eventPass.ticketCta')}
                <ChevronRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
              </span>
            </div>
          </button>
        ) : (
          <div className="grid grid-cols-1 gap-2 animate-pass-in md:grid-cols-2">
            <div className="flex flex-col gap-5 px-5 py-6 md:px-8 md:py-8">
              <div className="flex items-center justify-between">
                <Kicker t={t} />
                <button
                  type="button"
                  aria-label={t('eventPass.close')}
                  onClick={close}
                  className={cn('-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color,transform] duration-150 hover:bg-surface-container hover:text-on-surface active:scale-[0.96]', focusRing)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex flex-col gap-3">
                <label htmlFor={`${listId}-input`} className="font-serif text-2xl italic text-primary">{t('eventPass.searchLabel')}</label>
                <div className="relative">
                  <Search aria-hidden className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id={`${listId}-input`}
                    ref={inputRef}
                    value={query}
                    onChange={e => { setQuery(e.target.value); setSelected(null); setListOpen(true); }}
                    onFocus={() => {
                      setListOpen(true);
                      // On touch devices, lift the field to just under the sticky header so results sit above the keyboard
                      if (window.matchMedia('(pointer: coarse)').matches) {
                        window.setTimeout(() => {
                          const el = inputRef.current;
                          if (!el) return;
                          const top = el.getBoundingClientRect().top + window.scrollY - 88;
                          window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
                        }, 250);
                      }
                    }}
                    onBlur={() => setListOpen(false)}
                    onKeyDown={onKey}
                    role="combobox"
                    aria-expanded={showList}
                    aria-controls={`${listId}-list`}
                    aria-activedescendant={showList && matches.length ? `${listId}-opt-${active}` : undefined}
                    aria-autocomplete="list"
                    autoComplete="off"
                    placeholder={t('eventPass.searchPlaceholder')}
                    className="h-12 w-full rounded-xl bg-surface-container-lowest pl-11 pr-11 text-base text-on-surface outline-none transition-shadow duration-150 placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary"
                  />
                  {query && (
                    <button
                      type="button"
                      aria-label={t('eventPass.clear')}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setQuery(''); setSelected(null); setListOpen(true); inputRef.current?.focus(); }}
                      className="absolute right-1 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-on-surface"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {showList && (
                    <div className="absolute inset-x-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl bg-surface-container-lowest p-1 shadow-[0_1px_2px_hsl(var(--primary)/0.08),0_12px_32px_-8px_hsl(var(--primary)/0.25)] animate-in fade-in slide-in-from-top-1 duration-150">
                      {hint ? (
                        <p className="px-3 py-3 text-sm text-muted-foreground" aria-live="polite">{hint}</p>
                      ) : (
                        <ul id={`${listId}-list`} role="listbox" aria-label={t('eventPass.searchLabel')} style={{ maxHeight: listMax }} className="overflow-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
                          {matches.map((r, i) => (
                            <li
                              key={r.key}
                              id={`${listId}-opt-${i}`}
                              role="option"
                              aria-selected={i === active}
                              onMouseDown={e => e.preventDefault()}
                              onMouseEnter={() => { setActive(i); prefetch(r); }}
                              onClick={() => pick(r)}
                              className={cn(
                                'flex min-h-11 cursor-pointer flex-wrap items-center justify-between gap-x-3 gap-y-0.5 rounded-lg px-3 py-2',
                                i === active && 'bg-surface-container-low',
                              )}
                            >
                              <span className="text-base text-on-surface">{highlight(r.riderName)}</span>
                              <span className="text-sm tabular-nums text-muted-foreground">
                                {t('eventPass.classesCount', { count: r.classes.length })} · {t('eventPass.photosCount', { count: r.totalPhotos })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stub — concentric: outer 16px = 8px + 8px padding */}
            <div className="flex flex-col rounded-lg bg-surface-container-lowest p-6 text-on-surface md:p-8">
              {!selected ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 py-8 text-center">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-surface-container-low text-primary">
                    <Ticket className="h-5 w-5" />
                  </span>
                  <p className="max-w-[26ch] text-sm text-muted-foreground [text-wrap:balance]">{t('eventPass.admitOne')}</p>
                </div>
              ) : (
                <div key={selected.key} className="flex flex-1 flex-col gap-6 animate-pass-in">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{t('eventPass.holder')}</p>
                    <p className="mt-1 font-serif text-3xl italic leading-tight text-primary [text-wrap:balance]">{selected.riderName}</p>
                  </div>

                  {(() => {
                    const previews = passPhotos ? mixPreviews(passPhotos, 6) : [];
                    const more = selected.totalPhotos - previews.length;
                    if (!photosLoading && previews.length === 0) return null;
                    return (
                      <div className="grid grid-cols-3 gap-1.5">
                        {photosLoading
                          ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] rounded-md bg-surface-container-low motion-safe:animate-pulse" />)
                          : previews.map((u, i) => {
                              const ci = passPhotos!.findIndex(c => c.urls.includes(u));
                              const pi = passPhotos![ci].urls.indexOf(u);
                              return (
                                <button
                                  key={u}
                                  type="button"
                                  onClick={() => openGallery(ci, pi, 'preview')}
                                  aria-label={t('eventPass.viewAll', { count: selected.totalPhotos })}
                                  className={cn('relative aspect-[4/3] overflow-hidden rounded-md animate-pass-in', focusRing)}
                                  style={{ animationDelay: `${i * 40}ms` }}
                                >
                                  <img src={u} alt="" draggable={false} className="h-full w-full object-cover outline outline-1 -outline-offset-1 outline-[oklch(0_0_0/0.1)] transition-transform duration-300 hover:scale-[1.03]" />
                                  {!(i === previews.length - 1 && more > 0) && <PhotoLikeBadge count={likeCounts[photoIdFromUrl(u) ?? -1]} />}
                                  {i === previews.length - 1 && more > 0 && (
                                    <span className="absolute inset-0 flex items-center justify-center bg-primary/60 text-base font-semibold tabular-nums text-primary-foreground">+{more}</span>
                                  )}
                                </button>
                              );
                            })}
                      </div>
                    );
                  })()}
                  {passPhotos && passPhotos.some(c => c.urls.length) && (
                    <Button variant="outline" onClick={() => openGallery(0, 0, 'view_all')} className="w-full active:scale-[0.96]">
                      <Images /> {t('eventPass.viewAll', { count: selected.totalPhotos })}
                    </Button>
                  )}

                  <dl className="grid grid-cols-2 gap-2">
                    {[
                      { v: selected.classes.length, l: t('eventPass.classesCount', { count: selected.classes.length }).replace(/^\d+\s*/, '') },
                      { v: selected.totalPhotos, l: t('eventPass.photosCount', { count: selected.totalPhotos }).replace(/^\d+\s*/, '') },
                    ].map(x => (
                      <div key={x.l} className="flex flex-col-reverse rounded-lg bg-surface-container-low px-4 py-3">
                        <dt className="mt-1 text-sm text-muted-foreground">{x.l}</dt>
                        <dd className="font-serif text-3xl leading-none tabular-nums text-primary">{x.v}</dd>
                      </div>
                    ))}
                  </dl>

                  <ul className="flex max-h-64 flex-col gap-0.5 overflow-auto overscroll-contain">
                    {selected.classes.map((c, ci) => {
                      const urls = passPhotos?.find(p => p.participantId === c.participantId)?.urls || [];
                      return (
                        <li key={c.participantId}>
                          <button
                            type="button"
                            disabled={!urls.length}
                            onClick={() => openGallery(ci, 0, 'class_row')}
                            className={cn('flex w-full items-center gap-3 rounded-md p-1.5 text-left text-sm transition-colors duration-150 enabled:hover:bg-surface-container-low', focusRing)}
                          >
                            <span className="flex shrink-0 -space-x-3">
                              {urls.slice(0, 3).map(u => <img key={u} src={u} alt="" draggable={false} className="h-9 w-12 rounded object-cover ring-2 ring-surface-container-lowest" />)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-medium">{c.raceCategory}</span>
                              <span className="block tabular-nums text-muted-foreground">{t('eventPass.perClass', { photos: c.photos, horse: c.horseName })}</span>
                            </span>
                            {urls.length > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {pending > 0 && <div className="mt-auto"><Notice text={t('eventPass.pendingLong')} /></div>}
                  <div className="mt-auto rounded-lg bg-surface-container-low p-4">
                    {eligible ? (
                      <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                          <p className="text-sm tabular-nums text-muted-foreground line-through">{t('eventPass.insteadOf', { price: fullPrice })}</p>
                          <p className="mt-1 font-serif text-4xl leading-none tabular-nums text-primary">{chf(PRICES.EVENT_PASS)}</p>
                          {savings > 0 && (
                            <p className="mt-2 text-sm font-semibold tabular-nums text-success">{t('eventPass.save', { savings })}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleAdd}
                          disabled={inCart}
                          className={cn('inline-flex h-12 items-center gap-2 rounded-md bg-primary pl-5 pr-6 text-sm font-semibold text-primary-foreground transition-[transform,background-color] duration-150 hover:bg-primary-container active:scale-[0.96] disabled:cursor-default disabled:bg-primary-container', focusRing)}
                        >
                          <span aria-hidden className="relative h-4 w-4">
                            <Ticket className={crossFade} style={{ opacity: inCart ? 0 : 1, transform: `scale(${inCart ? 0.25 : 1})` }} />
                            <Check className={crossFade} style={{ opacity: inCart ? 1 : 0, transform: `scale(${inCart ? 1 : 0.25})` }} />
                          </span>
                          {inCart ? t('eventPass.added') : t('eventPass.add')}
                        </button>
                        <p className="basis-full text-xs text-muted-foreground">{t('eventPass.reassurance')}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        <p className="text-sm text-muted-foreground [text-wrap:pretty]">{t('eventPass.singleClassNote', { name: selected.riderName })}</p>
                        <button
                          type="button"
                          onClick={() => { trackEventPass('collection_link_clicked', { event_id: eventSwissId, source: 'event_ticket' }); onOpenRace(selected.classes[0].swissRaceId); }}
                          className={cn('inline-flex h-11 w-fit items-center gap-1 rounded-md bg-primary pl-5 pr-4 text-sm font-semibold text-primary-foreground transition-transform duration-150 active:scale-[0.96]', focusRing)}
                        >
                          {t('eventPass.openClass')} <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {selected && passPhotos && (
        <EventPassGallery
          open={!!gallery}
          onOpenChange={o => !o && setGallery(null)}
          riderName={selected.riderName}
          classes={passPhotos}
          start={gallery || undefined}
          inCart={inCart}
          onAdd={handleAdd}
          eventId={eventSwissId}
          eventName={eventName}
          source="event_ticket"
        />
      )}
    </section>
  );
};
